import { useState, useRef, useEffect } from "react";
import { Stage, Layer, Image as KonvaImage, Transformer } from "react-konva";
import useImage from "use-image";
import Konva from "konva";
import { X, Loader2, Save } from "lucide-react";

// Frameless per-photo editor: drag / resize / rotate the design directly on the
// mockup image (no area rectangle). Used to tweak a SINGLE product image without
// touching the mockup. onSave returns a composited PNG data URL.
export function FreeDesignEditor({
  mockupImageUrl,
  designUrl,
  canvasW,
  canvasH,
  initial,
  opacity = 1,
  onSave,
  onCancel,
}: {
  mockupImageUrl: string;
  designUrl: string;
  canvasW: number;
  canvasH: number;
  initial: { x: number; y: number; w: number; h: number; rotation: number };
  opacity?: number;
  onSave: (dataUrl: string) => Promise<void> | void;
  onCancel: () => void;
}) {
  const [bgImage] = useImage(mockupImageUrl, "anonymous");
  const [designImage] = useImage(designUrl, "anonymous");
  const stageRef = useRef<Konva.Stage>(null);
  const imageRef = useRef<Konva.Image>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const [selected, setSelected] = useState(true);
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(1);

  // Fit the design's natural aspect into the seeded box once the image loads.
  const [d, setD] = useState({ x: initial.x, y: initial.y, width: initial.w, height: initial.h, rotation: initial.rotation || 0 });
  const fittedRef = useRef(false);
  useEffect(() => {
    if (fittedRef.current || !designImage) return;
    fittedRef.current = true;
    const natW = designImage.naturalWidth, natH = designImage.naturalHeight;
    if (natW && natH) {
      const ar = initial.w / initial.h, dr = natW / natH;
      let w: number, h: number;
      if (dr > ar) { w = initial.w; h = initial.w / dr; } else { h = initial.h; w = initial.h * dr; }
      setD({ x: initial.x + (initial.w - w) / 2, y: initial.y + (initial.h - h) / 2, width: w, height: h, rotation: initial.rotation || 0 });
    }
  }, [designImage, initial]);

  const maxW = Math.min(canvasW, window.innerWidth * 0.78);
  const maxH = Math.min(canvasH, window.innerHeight * 0.7);
  const fitScale = Math.min(1, maxW / canvasW, maxH / canvasH);
  const scale = fitScale * zoom;

  useEffect(() => {
    if (selected && transformerRef.current && imageRef.current) {
      transformerRef.current.nodes([imageRef.current]);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [selected, designImage, d]);

  async function handleSave() {
    const stage = stageRef.current;
    if (!stage) return;
    setSaving(true);
    try {
      // Export at the mockup's native resolution, capped at ~2000px longest side.
      setSelected(false);
      await new Promise((r) => setTimeout(r, 30));
      const exportPR = Math.min(1, 2000 / Math.max(canvasW, canvasH));
      const dataUrl = stage.toDataURL({ pixelRatio: exportPR, mimeType: "image/png" });
      await onSave(dataUrl);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl flex flex-col" style={{ maxWidth: canvasW * scale + 48, maxHeight: "95vh" }}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-gray-900">Bu Fotoğrafta Düzenle</h2>
            <p className="text-xs text-gray-500 mt-0.5">Tasarımı sürükle, köşelerden boyutlandır, üst tutamaç ile döndür. Yalnız bu görsele uygulanır.</p>
          </div>
          <button onClick={onCancel} className="p-2 rounded-lg hover:bg-gray-100"><X className="h-5 w-5 text-gray-500" /></button>
        </div>

        <div className="flex items-center justify-end gap-1.5 px-5 py-1.5 border-b border-gray-100 bg-white">
          <span className="text-[10px] text-gray-400 mr-auto">Sürükle: taşı · Köşeler: boyutlandır · Üst tutamaç: döndür</span>
          <button type="button" onClick={() => setZoom((z) => Math.max(0.25, +(z - 0.25).toFixed(2)))} className="w-6 h-6 flex items-center justify-center rounded border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm leading-none">−</button>
          <span className="text-[11px] text-gray-600 w-11 text-center tabular-nums">{Math.round(scale * 100)}%</span>
          <button type="button" onClick={() => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))} className="w-6 h-6 flex items-center justify-center rounded border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm leading-none">+</button>
          <button type="button" onClick={() => setZoom(1)} className="ml-1 px-2 h-6 flex items-center justify-center rounded border border-gray-200 text-[11px] text-gray-600 hover:bg-gray-50">Sığdır</button>
        </div>

        <div className="flex-1 grid place-items-center bg-gray-50 p-4 overflow-auto">
          <div className="rounded-lg overflow-hidden shadow-sm border border-gray-200" style={{ width: canvasW * scale, height: canvasH * scale }}>
            <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>
              <Stage ref={stageRef} width={canvasW} height={canvasH}
                onMouseDown={(e) => { if (e.target === e.target.getStage()) setSelected(false); }}>
                <Layer>
                  {bgImage && <KonvaImage image={bgImage} x={0} y={0} width={canvasW} height={canvasH} listening={false} />}
                  {designImage && (
                    <KonvaImage
                      ref={imageRef}
                      image={designImage}
                      x={d.x} y={d.y} width={d.width} height={d.height} rotation={d.rotation}
                      opacity={opacity}
                      draggable
                      onClick={() => setSelected(true)}
                      onTap={() => setSelected(true)}
                      onDragEnd={(e) => setD((p) => ({ ...p, x: e.target.x(), y: e.target.y() }))}
                      onTransformEnd={() => {
                        const node = imageRef.current;
                        if (!node) return;
                        const sx = node.scaleX(), sy = node.scaleY();
                        node.scaleX(1); node.scaleY(1);
                        setD({ x: node.x(), y: node.y(), width: Math.max(20, node.width() * sx), height: Math.max(20, node.height() * sy), rotation: node.rotation() });
                      }}
                    />
                  )}
                  {selected && designImage && (
                    <Transformer
                      ref={transformerRef}
                      keepRatio
                      rotateEnabled
                      enabledAnchors={["top-left", "top-right", "bottom-left", "bottom-right"]}
                      boundBoxFunc={(o, n) => (n.width < 20 || n.height < 20 ? o : n)}
                      anchorSize={14} anchorCornerRadius={3} anchorStroke="#6366f1" anchorFill="#fff" anchorStrokeWidth={2}
                      borderStroke="#6366f1" borderStrokeWidth={2} rotateAnchorOffset={50} rotateAnchorCursor="grab" padding={0}
                    />
                  )}
                </Layer>
              </Stage>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">İptal</button>
          <button onClick={handleSave} disabled={saving || !designImage}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Kaydet (bu görsel)
          </button>
        </div>
      </div>
    </div>
  );
}
